import React, { useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { BoxBalance, DirectAccess, YourActivity } from 'components'
import { getUserBalance } from 'store/transfers/thunks'
import WalletDesktopSkeleton from 'assets/skeletons/WalletDesktopSkeleton'
import useNavigation from 'hooks/useNavigation'
import { SCREEN_TITLES } from 'utils/navigation'
import Helmet from 'components/utilities/Helmet'
import { getMultiEnterpriseAssociations } from 'store/menu/thunks'
import image from '@qirapagos/web/src/assets/images/dynamic-banner.gif'
import { useAppSelector } from 'hooks/redux'
import {
  Container,
  ViewLeftColumn,
  ViewRightColumn,
  ViewColumnsContainer,
  ViewSubContainer,
  CarouselWrapper,
  CarouselSlides,
  CarouselSlide,
  ViewBannerContainer,
  ImageAdvertising
} from './styles'
import { getActivity } from 'store/activity/thunks'
import { getNotifications } from 'store/notifications/thunks'
import { fetchSettings } from 'store/settings/thunks'
import { onPushNotificationCallback } from 'config/firebase'
import BoxInvestment from 'components/molecules/BoxInvestment'
import {
  getFund,
  getHoldingAccount,
  getInvestmentClient,
  getInvestmentParams,
  getIsTutorialCompleted,
  getLastPerformance,
  getOrders
} from 'store/investments/thunks'
import { useScreenSizes } from 'hooks/useScreenSizes'
import InvestmentNews from 'components/organisms/InvestmentNews'
import { InvestmentActionType, InvestmentStatus } from '../Investments/types'

const Wallet: React.FC = () => {
  const { isLoading, balance } = useAppSelector(state => state.transfers)
  const { isLoading: isLoadingAuth } = useAppSelector(state => state.auth)
  const { isLoading: isLoadingActivity, activity } = useAppSelector(
    state => state.activity
  )
  const { loadingPending } = useAppSelector(state => state.legalDocument)
  const { multiEnterpriseAssociation } = useAppSelector(state => state.menu)
  const {
    tutorialCompleted,
    lastPerformance,
    authorization,
    investmentClient,
    balanceHoldingAccount,
    loadingHoldingAccount,
    isBalanceHoldingError
  } = useAppSelector(store => store.investments)
  const { isMobileDisplay } = useScreenSizes()
  const dispatch = useDispatch()
  const { onNavigate } = useNavigation()
  const noBalanceHoldingAccount = Number(balanceHoldingAccount) === 0
  const disabledAction = investmentClient?.statusId !== InvestmentStatus.APPROVED

  useEffect(() => {
    onPushNotificationCallback(() => {
      dispatch(getNotifications())
    })
    dispatch(getInvestmentParams())
    dispatch(getFund())
    dispatch(fetchSettings())
  }, [dispatch])

  useEffect(() => {
    dispatch(getActivity(!!activity))
    dispatch(getHoldingAccount())
    dispatch(getIsTutorialCompleted())
    dispatch(getInvestmentClient())
    dispatch(getLastPerformance())
    dispatch(getOrders())
    if (Number(balance) === 0) {
      dispatch(getUserBalance())
    }
    if (!multiEnterpriseAssociation) {
      dispatch(getMultiEnterpriseAssociations())
    }
  }, [isLoadingAuth])

  const goToMyData = () => {
    onNavigate('/content/my-data', { hasBackArrow: true })
  }

  const goToInvestmentOperation = (title: string) => {
    onNavigate('/content/investments/investment-operation', {
      title,
      hasBackArrow: true
    })
  }

  const goToInvestmentSubscribe = () => {
    goToInvestmentOperation('Suscripciones')
  }

  const goToInvestmentRescue = () => {
    goToInvestmentOperation('Rescates')
  }

  if (
    loadingPending ||
    loadingHoldingAccount ||
    isLoading ||
    isLoadingActivity ||
    isLoadingAuth ||
    !activity
  ) {
    return <WalletDesktopSkeleton />
  }

  return (
    <Container>
      <Helmet title={SCREEN_TITLES.WALLET} />
      <ViewSubContainer>
        <ViewColumnsContainer>
          <ViewLeftColumn>
            {isMobileDisplay
              ? (
                <CarouselWrapper>
                  <CarouselSlides>
                    <CarouselSlide>
                      <BoxBalance
                        title="Dinero disponible"
                        buttonText="Mis datos"
                        onPressButton={goToMyData}
                        showCopyIcon
                        height="165px"
                        action={tutorialCompleted && authorization ? InvestmentActionType.INVEST : undefined}
                        onPressAction={tutorialCompleted && authorization ? goToInvestmentSubscribe : undefined}
                        disabledAction={disabledAction}
                        showEyeButton
                      />
                    </CarouselSlide>
                    {!tutorialCompleted || !authorization
                      ? (
                        <CarouselSlide>
                          <InvestmentNews isMobile />
                        </CarouselSlide>
                        )
                      : (
                        <CarouselSlide>
                          <BoxInvestment
                            title="Dinero invertido"
                            lastPerformance={lastPerformance}
                            amount={balanceHoldingAccount}
                            balanceHoldingError={isBalanceHoldingError}
                            height="165px"
                            action={InvestmentActionType.RESCUE}
                            onPressButton={goToInvestmentRescue}
                            disabledAction={noBalanceHoldingAccount || disabledAction}
                            showEyeButton
                          />
                        </CarouselSlide>
                        )}
                  </CarouselSlides>
                </CarouselWrapper>
                )
              : (
                <>
                  <BoxBalance
                    title="Dinero disponible"
                    buttonText="Mis datos"
                    onPressButton={goToMyData}
                    showCopyIcon
                    height="165px"
                    action={tutorialCompleted && authorization ? InvestmentActionType.INVEST : undefined}
                    disabledAction={disabledAction}
                    onPressAction={tutorialCompleted && authorization ? goToInvestmentSubscribe : undefined}
                    showEyeButton
                  />
                  {tutorialCompleted && authorization && (
                    <BoxInvestment
                      title="Dinero invertido"
                      lastPerformance={lastPerformance}
                      amount={balanceHoldingAccount}
                      balanceHoldingError={isBalanceHoldingError}
                      height="165px"
                      action={InvestmentActionType.RESCUE}
                      onPressButton={goToInvestmentRescue}
                      disabledAction={noBalanceHoldingAccount || disabledAction}
                      showEyeButton
                    />
                  )}
                </>
                )}
            {(!tutorialCompleted || !authorization) && !isMobileDisplay && (
              <InvestmentNews />
            )}
            <DirectAccess />
            <ViewBannerContainer>
              <a
                target="_blank"
                href="https://www.qiraglobal.com/"
                rel="noreferrer"
              >
                <ImageAdvertising src={image} alt="Advertising" />
              </a>
            </ViewBannerContainer>
          </ViewLeftColumn>
          <ViewRightColumn>
            <YourActivity />
          </ViewRightColumn>
        </ViewColumnsContainer>
      </ViewSubContainer>
    </Container>
  )
}
export default Wallet
